import { useRef, useState, type KeyboardEvent } from 'react';

import { isActivateKey } from './activate';
import { cx } from './classNames';

/**
 * Monatskalender für `DatePicker` und `DateRangePicker`. Woche beginnt Montag,
 * deutsche Wochentagskürzel (REQ-06 §3).
 */
export interface CalendarProps {
  selected?: Date | null;
  /** Bereichsauswahl: Anfang und Ende werden hervorgehoben. */
  rangeStart?: Date | null;
  rangeEnd?: Date | null;
  onSelect: (date: Date) => void;
}

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const CELL_COUNT = 42;
const titleFormat = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });

/** Schrittweite der Pfeiltasten im Tagesraster: eine Spalte bzw. eine Woche. */
const ARROW_STEP: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -7,
  ArrowDown: 7,
};

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function isSameDay(a: Date, b: Date): boolean {
  return startOfDay(a).getTime() === startOfDay(b).getTime();
}

function isInRange(day: Date, start: Date | null | undefined, end: Date | null | undefined) {
  if (start == null || end == null) return false;
  const time = startOfDay(day).getTime();
  return time >= startOfDay(start).getTime() && time <= startOfDay(end).getTime();
}

export function Calendar(props: CalendarProps) {
  const initial = props.selected ?? props.rangeStart ?? new Date();
  const [viewMonth, setViewMonth] = useState(
    () => new Date(initial.getFullYear(), initial.getMonth(), 1),
  );
  // Ein Tabstopp für das ganze Raster („roving tabindex"): 42 einzelne
  // Tabstopps wären unbenutzbar. `null` heißt „noch nicht bewegt" — dann gilt
  // der aus der Auswahl abgeleitete Startpunkt weiter, auch nach Monatswechsel.
  const [movedIndex, setMovedIndex] = useState<number | null>(null);
  const cellRefs = useRef<(HTMLTableCellElement | null)[]>([]);

  const today = new Date();
  const leading = (viewMonth.getDay() + 6) % 7;
  const firstCell = addDays(viewMonth, -leading);
  const days = Array.from({ length: CELL_COUNT }, (_unused, index) => addDays(firstCell, index));
  const weeks = Array.from({ length: CELL_COUNT / 7 }, (_unused, week) =>
    days.slice(week * 7, week * 7 + 7),
  );

  const inViewMonth = (day: Date) => day.getMonth() === viewMonth.getMonth();
  const startIndex = (() => {
    const selected = props.selected ?? props.rangeStart ?? null;
    const bySelected = selected === null ? -1 : days.findIndex((day) => isSameDay(day, selected));
    if (bySelected !== -1) return bySelected;
    const byToday = days.findIndex((day) => isSameDay(day, today) && inViewMonth(day));
    return byToday === -1 ? days.findIndex(inViewMonth) : byToday;
  })();
  // `Math.max` sichert nur ab: die 42 Zellen enthalten stets den ganzen Monat.
  const activeIndex = movedIndex ?? Math.max(startIndex, 0);

  const shiftMonth = (delta: number) => {
    setMovedIndex(null);
    setViewMonth((month) => new Date(month.getFullYear(), month.getMonth() + delta, 1));
  };

  const onDayKeyDown = (event: KeyboardEvent<HTMLTableCellElement>, day: Date, index: number) => {
    if (isActivateKey(event.key)) {
      event.preventDefault();
      props.onSelect(startOfDay(day));
      return;
    }
    const step = ARROW_STEP[event.key];
    if (step === undefined) return;
    event.preventDefault();
    const next = index + step;
    if (next < 0 || next >= CELL_COUNT) return;
    setMovedIndex(next);
    cellRefs.current[next]?.focus();
  };

  return (
    <div className="e-calendar">
      <div className="e-header e-month">
        <span className="e-title">{titleFormat.format(viewMonth)}</span>
        <div className="e-icon-container">
          <button
            type="button"
            className="e-prev"
            aria-label="Vorheriger Monat"
            onClick={() => {
              shiftMonth(-1);
            }}
          >
            <span className="e-icons fa-solid fa-chevron-left" />
          </button>
          <button
            type="button"
            className="e-next"
            aria-label="Nächster Monat"
            onClick={() => {
              shiftMonth(1);
            }}
          >
            <span className="e-icons fa-solid fa-chevron-right" />
          </button>
        </div>
      </div>
      <div className="e-content e-month">
        <table role="grid">
          <thead className="e-week-header">
            <tr>
              {WEEKDAYS.map((weekday) => (
                <th key={weekday} abbr={weekday}>
                  {weekday}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={week[0]?.getTime()}>
                {week.map((day, weekdayIndex) => {
                  const index = weekIndex * 7 + weekdayIndex;
                  const selected =
                    (props.selected != null && isSameDay(day, props.selected)) ||
                    isInRange(day, props.rangeStart, props.rangeEnd) ||
                    (props.rangeStart != null &&
                      props.rangeEnd == null &&
                      isSameDay(day, props.rangeStart));
                  return (
                    <td
                      key={day.getTime()}
                      ref={(element) => {
                        cellRefs.current[index] = element;
                      }}
                      role="gridcell"
                      aria-selected={selected}
                      tabIndex={index === activeIndex ? 0 : -1}
                      className={cx(
                        'e-cell',
                        !inViewMonth(day) && 'e-other-month',
                        isSameDay(day, today) && 'e-today',
                        selected && 'e-selected',
                      )}
                      onClick={() => {
                        props.onSelect(startOfDay(day));
                      }}
                      onKeyDown={(event) => {
                        onDayKeyDown(event, day, index);
                      }}
                    >
                      <span className="e-day">{day.getDate()}</span>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="e-footer-container">
        <button
          type="button"
          className="e-btn e-flat e-today"
          onClick={() => {
            props.onSelect(startOfDay(new Date()));
          }}
        >
          Heute
        </button>
      </div>
    </div>
  );
}

export default Calendar;
